/**
 * Locale-aware href building, in its own leaf module.
 *
 * `locale-navigation.js` imports `@solidjs/router` for `useLocaleNavigate`, so importing this helper
 * from there drags the router into whatever imports it.
 *
 * Not because either module THROWS on the server — an earlier note here claimed the router calls the
 * client-only `template()` at module scope, and that was retested and is false. Under the `solid`
 * export condition the prerender resolves with, `@solidjs/router` is `dist/index.jsx`, which names
 * `template(` nowhere; it and `locale-navigation.js` both import cleanly under the SSR loader.
 * `prerender-import-guard.js` says the same thing at its top.
 *
 * What the router costs is the prerender BUILD: that guard forbids it from the prerender graph
 * outright, because what the router supplies is RENDER-time context — `useParams()` throws without a
 * live `<Router />`, and a prerender has no location to give one. Components that only need an href
 * import from here and stay out of that graph. `locale-navigation.js` re-exports it so existing
 * callers are unaffected.
 */

import {
    buildPathnameForLocale,
    getLocaleFromPathname,
    getPathWithoutLocalePrefix,
} from './locale-path.js'
import { isProtocolPath } from './path-utils.js'

/**
 * Get a locale-aware href for use in links
 *
 * Usage:
 *   const href = getLocaleHref('/account')  // Returns '/fr/account' if locale is 'fr'
 *   const href = getLocaleHref('/account?foo=bar#section')  // Preserves query and hash
 *
 * @param {string} path - Path to convert (may include query params and hash)
 * @returns {string} Path with locale prefix, preserving query params and hash
 */
export function getLocaleHref (path) {
    // This is a helper function that can be used outside of components
    // For component usage, use useLocale().addLocalePrefix() instead

    // Don't modify protocol URLs (mailto:, http://, https://, etc.)
    if (isProtocolPath(path)) {
        return path
    }

    const [
        pathBeforeQuery,
        queryString,
    ] = path.split('?')
    const [
        pathOnly,
        hash,
    ] = pathBeforeQuery.split('#')

    const currentPath = typeof window === 'undefined' ? '' : window.location.pathname
    const urlLocale = getLocaleFromPathname(currentPath)

    const normalizedPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
    const pathWithoutLocale = getPathWithoutLocalePrefix(normalizedPath)
    const newPath = buildPathnameForLocale(pathWithoutLocale, urlLocale)

    return newPath + (queryString ? `?${queryString}` : '') + (hash ? `#${hash}` : '')
}

/**
 * The same href, for a locale given explicitly rather than read from the URL.
 *
 * `getLocaleHref` infers the locale from `window.location`, which does not exist during a server
 * render — every link then silently comes out in the default locale. A prerendered `fr-ca` page
 * linking to `/newsroom/x` instead of `/fr-ca/newsroom/x` drops the visitor into English, so any
 * component that renders on the server passes the locale it was given (`useIntl().locale`).
 *
 * @param {string} path
 * @param {string} locale
 * @returns {string}
 */
export function buildLocaleHref (path, locale) {
    if (isProtocolPath(path)) {
        return path
    }

    const [
        pathBeforeQuery,
        queryString,
    ] = path.split('?')
    const [
        pathOnly,
        hash,
    ] = pathBeforeQuery.split('#')
    const normalizedPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`
    const newPath = buildPathnameForLocale(getPathWithoutLocalePrefix(normalizedPath), locale)

    return newPath + (queryString ? `?${queryString}` : '') + (hash ? `#${hash}` : '')
}
